import { NormalizedNumber } from '@sparkdotfi/common-universal'
import { useSuspenseQuery } from '@tanstack/react-query'
import { useConfig } from 'wagmi'

import { marketBalances } from './marketBalances'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { useConnectedAddress } from '@/domain/wallet/useConnectedAddress'
import { useMarketInfo } from '@/features/sparklend/common/data/market-info/useMarketInfo'
import { SparkLendToken } from '@/features/sparklend/tokens/SparkLendToken'

export interface WalletBalance {
  balance: NormalizedNumber
  token: SparkLendToken
}

export interface MarketWalletInfoParams {
  chainId: number
}

export interface MarketWalletInfo {
  isConnected: boolean
  walletBalances: WalletBalance[]

  findWalletBalanceForToken: (token: SparkLendToken) => NormalizedNumber
  findWalletBalanceForSymbol: (symbol: TokenSymbol) => NormalizedNumber
}

export function useMarketWalletInfo({ chainId }: MarketWalletInfoParams): MarketWalletInfo {
  const { account } = useConnectedAddress({ throwOnNotConnected: false })
  const wagmiConfig = useConfig()
  const { marketInfo } = useMarketInfo({ chainId })

  const { data: balanceData } = useSuspenseQuery({
    ...marketBalances({
      wagmiConfig,
      account,
      chainId,
    }),
  })

  const walletBalances: WalletBalance[] = balanceData
    .map((balanceItem) => {
      const token = marketInfo.findReserveByUnderlyingAsset(balanceItem.address)?.token
      if (!token) {
        return {
          balance: undefined,
          token: undefined,
        }
      }

      return {
        balance: token.fromBaseUnit(balanceItem.balanceBaseUnit),
        token,
      }
    })
    .filter((r) => r.token)
    // @note: this map is here only to make TS happy :(
    .map((r) => ({
      balance: r.balance!,
      token: r.token!,
    }))

  /* eslint-disable func-style */
  const findWalletBalanceForToken = (token: SparkLendToken): NormalizedNumber => {
    return findWalletBalanceForSymbol(token.symbol)
  }
  const findWalletBalanceForSymbol = (symbol: TokenSymbol): NormalizedNumber => {
    const aTokenReserve = marketInfo.findReserveByATokenSymbol(symbol)
    if (aTokenReserve) {
      return aTokenReserve.aTokenBalance
    }

    return walletBalances.find((wb) => wb.token.symbol === symbol)?.balance ?? NormalizedNumber.ZERO
  }
  /* eslint-enable func-style */

  return {
    walletBalances,
    isConnected: account !== undefined,
    findWalletBalanceForToken,
    findWalletBalanceForSymbol,
  }
}
